import React, { Component } from 'react';
import { geoStream, geoPath } from 'd3-geo';
import { geoNaturalEarth as geoProjection } from 'd3-geo-projection';
import { feature as topojsonFeature, mergeArcs as topojsonMergeArcs } from 'topojson';
import partition from 'lodash/partition';
import keyBy from 'lodash/keyBy';
import without from 'lodash/without';

import MapCountries from '../map-countries/map-countries.jsx';
import MapMarkersLayer from '../MapMarkersLayer/index.js';
import MapBackground from '../MapBackground/svg.js';
import MapLabelsLayer from '../MapLabelsLayer/index.js';
import ZoomRegionsDebugLayer from '../ZoomRegionsDebugLayer/index.js';

import { DimensionsContext } from '../dimensions/dimensions.jsx';

import { matchBreakpoint } from '@chh/ui/js/matchBreakpoint.js';
import { getBoundsGeoPolygon, getZoomTransformFromBounds } from '../../utilities/geometry.js';

import {
  TEST_REGION_BOUNDS,
  POLICIES_EU27_COUNTRYGROUP_COUNTRY_IDS,
  COUNTRY_ID_M49_TO_COMTRADE,
} from '../../constants.js';

import './styles.scss';

import world from '../../assets/combined.json';

let countriesFeature;

function getCountriesFeature() {
  if (countriesFeature) {
    return countriesFeature;
  }

  // FIXME: This probably mutates the data, although this may not matter
  const countries = world.objects.countries;

  countries.geometries = countries.geometries.filter(function (geometry) {
    return geometry.id !== '010';
  });

  // Merge EU-27 countries into single boundary
  /*
  const [euGeometries, otherGeometries] = partition(countries.geometries, function (geometry) {
    return POLICIES_EU27_COUNTRYGROUP_COUNTRY_IDS.includes(geometry.id);
  });

  const euGeometry = topojsonMergeArcs(world, euGeometries);

  countries.geometries = [...otherGeometries, euGeometry];
  */

  countriesFeature = topojsonFeature(world, countries);
  return countriesFeature;
}

// Refactor to reduce duplication from Map component
export default class ChoroplethMap extends Component {
  static contextType = DimensionsContext;

  constructor(props, context) {
    super();

    /*
    // FIXME: This probably mutates the data, although this may not matter
    const countries = world.objects.countries;

    countries.geometries = countries.geometries.filter(function (geometry) {
      return geometry.id !== 'AQ' && geometry.id.length >= 2;
    });

    const [euGeometries, otherGeometries] = partition(countries.geometries, function (geometry) {
      return EU27_IDS.includes(geometry.id);
    });

    const euGeometry = topojsonMergeArcs(world, euGeometries);

    countries.geometries = [...otherGeometries, euGeometry];

    this.countries = topojsonFeature(world, countries);
    */

    this.countries = getCountriesFeature();

    this.countryPoints = topojsonFeature(world, world.objects.points);

    // Skip Antarctica
    // FIXME: Remove from data so we don't load it?
    // This breaks the projection bounds
    // this.countries.features = this.countries.features.filter(function (feature) {
    //   return feature.id !== 'AQ' || feature.id.length > 2;
    // });

    this.projection = this.getProjection(context.width, context.height);
    this.originalProjectionScale = this.projection.scale();
    this.originalProjectionTranslate = this.projection.translate();

    console.log('projection', this.projection.scale(), this.projection.translate());

    this.state = {
      transform: null,
      debug: false,
    };
  }

  componentWillUpdate(nextProps, nextState, nextContext) {
    if (this.context.width !== nextContext.width || this.context.height !== nextContext.height) {
      this.projection = this.getProjection(nextContext.width, nextContext.height);
      this.originalProjectionScale = this.projection.scale();
      this.originalProjectionTranslate = this.projection.translate();
    }
  }

  getProjection(width, height) {
    return geoProjection().fitSize([width, height], this.countries).rotate([-11.5, 0, 0]);
  }

  // TODO: Need to defer rendering defs, flows and hitboxes until labels
  // have been laid out?
  render(props, state, context) {
    const enableInteraction = matchBreakpoint('tablet').matches;
    // Labels are otherwise hidden below tablet, but the image export renders a
    // fixed desktop-size copy off-screen while still on a mobile viewport, so it
    // forces them on to match the exported layout.
    const showLabels = enableInteraction || props.forceLabels;
    let width = context.trueWidth;
    let height = context.trueHeight;
    let viewboxWidth = context.width;
    let viewboxHeight = context.height;

    if (props.width && props.height) {
      width = viewboxWidth = props.width;
      height = viewboxHeight = props.height;
      this.projection = this.getProjection(width, height);
    }

    let transformObj = { transform: null, transformRaw: null };
    if (props.zoomCountries) {
      transformObj = this.getCountryPointBoundsZoomTransform(props.zoomCountries);
      console.log('transformObj', transformObj);
    }

    let dataByCountryId;
    if (props.data) {
      dataByCountryId = keyBy(props.data, 'country.id');
    }

    return (
      <div className="chloropleth-map" style={{ width: `${width}px`, height: `${height}px` }}>
        <MapBackground />
        <svg className="chloropleth-map__svg" viewBox={`0 0 ${viewboxWidth} ${viewboxHeight}`}>
          <g transform={transformObj.transform}>
            <g>
              <MapBackground x={0} y={0} width={viewboxWidth} height={viewboxHeight} />
            </g>
            <g>
              <MapCountries
                countries={this.countries.features.map((feature) => {
                  const featureId = parseInt(feature.id, 10);
                  const countryId = COUNTRY_ID_M49_TO_COMTRADE[featureId] || featureId;
                  const datum = dataByCountryId[countryId];
                  return {
                    ...feature,
                    colour: datum ? datum.colour : undefined,
                    filled: !!datum || props.filledCountryIds?.includes(countryId),
                  };
                })}
                countryPoints={this.countryPoints.features.map((feature) => {
                  const featureId = parseInt(feature.id, 10);
                  const countryId = COUNTRY_ID_M49_TO_COMTRADE[featureId] || featureId;
                  const datum = dataByCountryId[countryId];
                  return {
                    ...feature,
                    colour: datum ? datum.colour : undefined,
                    filled: !!datum || props.filledCountryIds?.includes(countryId),
                  };
                })}
                projection={this.projection}
                transform={transformObj.transformRaw}
                renderDots={showLabels}
                onHoverCountry={props.onHoverCountry}
                onClickCountry={props.onClickCountry}
              />
            </g>
            {state.debug && (
              <ZoomRegionsDebugLayer
                regionBounds={TEST_REGION_BOUNDS}
                projection={this.projection}
              />
            )}
          </g>
          {showLabels && (
            <MapLabelsLayer
              projection={this.projection}
              transform={transformObj.transformRaw}
              countries={
                props.activeCountry && !props.countries.includes(props.activeCountry)
                  ? [...props.countries, props.activeCountry]
                  : props.countries
              }
              activeCountryIds={props.activeCountryIds}
              hoveredCountries={props.hoveredCountries}
              showAllLabels={props.showAllLabels}
              onHover={props.onHoverCountry}
              onClick={props.onClickCountry ? props.onClickCountry : null}
            />
          )}
        </svg>
      </div>
    );
  }

  handleChangeRegion = (event) => {
    console.log(event.currentTarget.value);
    this.setTransformToRegion(event.currentTarget.value);
  };

  handleChangeDebug = (event) => {
    this.setState({
      debug: event.target.checked,
    });
  };

  getCountryPointBoundsZoomTransform(countries, context) {
    if (!countries || !countries.length) {
      return { transform: null, transformRaw: null };
    }

    context = context || this.context;

    console.log('get zoom transform', countries);

    const coordinates = countries.map((country) => {
      return [country.model.lng, country.model.lat];
    });
    const geojson = { type: 'MultiPoint', coordinates: coordinates };

    const path = geoPath().projection(this.projection);
    const pathBounds = path.bounds(geojson);

    return getZoomTransformFromBounds(pathBounds, context, 0.2, this.props.maxZoomScale);
  }
}
